"use client";

import { cn } from "@/lib/utils";
import React, { useRef, useState, useEffect } from "react";

/**
 * ScrollableContainer
 *
 * A reusable UI component that provides a scrollable area with optional gradient fades at the top and bottom edges.
 * The gradients appear automatically when the content overflows the container, visually indicating that more content is available by scrolling.
 *
 * Features:
 * - Automatically detects overflow and shows/hides gradients accordingly.
 * - Gradients at the top and bottom fade in/out based on scroll position.
 * - Customizable maxHeight, container styling, and gradient styling via props.
 * - Responsive and accessible: gradients are aria-hidden and do not interfere with content interaction.
 * - Useful for long lists, menus, or any content that may exceed the visible area.
 *
 * Props:
 * @param children ReactNode – The scrollable content.
 * @param className? string – Additional classes for the scrollable area.
 * @param maxHeight? string – CSS max-height for the scrollable area (default: "calc(100vh - 200px)").
 * @param topGradientClassName? string – Additional classes for the top gradient.
 * @param bottomGradientClassName? string – Additional classes for the bottom gradient.
 * @param containerClassName? string – Additional classes for the outer container.
 *
 * Example usage:
 * <ScrollableContainer maxHeight="400px">
 *   <div>...long content...</div>
 * </ScrollableContainer>
 *
 * This component is ideal for use in modals, sidebars, or any area where you want to keep a fixed height and indicate overflow elegantly.
 */
export function ScrollableContainer({
  children,
  className,
  maxHeight = "calc(100vh - 200px)",
  topGradientClassName,
  bottomGradientClassName,
  containerClassName,
}: {
  children: React.ReactNode;
  className?: string;
  maxHeight?: string;
  topGradientClassName?: string;
  bottomGradientClassName?: string;
  containerClassName?: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [showTopGradient, setShowTopGradient] = useState(false);
  const [showBottomGradient, setShowBottomGradient] = useState(false);

  // Aktualisiert die Gradient-Anzeige basierend auf der Scroll-Position
  const handleScroll = () => {
    const container = containerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;

    // Zeigt den oberen Gradient, wenn nicht ganz oben
    setShowTopGradient(scrollTop > 10);

    // Zeigt den unteren Gradient, wenn nicht ganz unten
    setShowBottomGradient(scrollTop + clientHeight < scrollHeight - 10);
  };

  // Initial und bei Änderung der Kinder auf Overflow prüfen
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Prüft, ob Container scrollbar ist (Inhalt größer als Container)
    const checkOverflow = () => {
      const hasOverflow = container.scrollHeight > container.clientHeight;
      setShowBottomGradient(hasOverflow);
    };

    checkOverflow();

    // Initialen Scroll-Handler ausführen
    handleScroll();

    // Event-Listener für Resize hinzufügen
    window.addEventListener("resize", checkOverflow);

    return () => {
      window.removeEventListener("resize", checkOverflow);
    };
  }, [children]);

  return (
    <div className={cn("relative h-full", containerClassName)}>
      {/* Oberer Gradient */}
      <div
        className={cn(
          "from-background pointer-events-none absolute top-0 right-0 left-0 z-10 h-8 bg-gradient-to-b to-transparent opacity-0 transition-opacity duration-200",
          showTopGradient ? "opacity-100" : "opacity-0",
          topGradientClassName
        )}
        aria-hidden="true"
      />

      {/* Scrollbarer Container */}
      <div
        ref={containerRef}
        className={cn("-mx-4 h-full overflow-y-auto px-4 py-2", className)}
        style={{ maxHeight }}
        onScroll={handleScroll}
      >
        {children}
      </div>

      {/* Unterer Gradient */}
      <div
        className={cn(
          "from-background pointer-events-none absolute right-0 bottom-0 left-0 z-10 h-12 bg-gradient-to-t to-transparent opacity-0 transition-opacity duration-200",
          showBottomGradient ? "opacity-100" : "opacity-0",
          bottomGradientClassName
        )}
        aria-hidden="true"
      />
    </div>
  );
}
